/**
 * ModelService
 *
 * Handles LLM model selection, formatting, and fetching from API.
 * Includes logic for provider-specific defaults and fallback models.
 *
 * Extracted from: VideoConfigForm.tsx (lines 94-99, 206-295, 475-508)
 */

import type { LLMModelOption, LLMProvider, SelectOption } from '../types';
import { DEFAULT_VALUES, LLM_MODELS } from '../constants';
import { getAuthHeadersFromStorage } from '../utils/httpUtils';
import { isServerlessMode } from '../utils/apiMode';
import { getModelTotalPrice } from '@/constants/llm/modelAttributes';

export class ModelService {
  /**
   * Gets the initial/default model for a given LLM provider
   *
   * @param provider - LLM provider (e.g., "openrouter", "ollama")
   * @returns Default model ID for the provider
   *
   * @example
   * getInitialModel("ollama") // Returns "gemma:7b"
   * getInitialModel("openrouter") // Returns DEFAULT_VALUES.LLM_MODEL
   */
  static getInitialModel(provider: LLMProvider): string {
    if (provider === 'ollama') {
      return 'gemma:7b'; // Safe default for Ollama
    }
    const defaultModel = DEFAULT_VALUES.LLM_MODEL;
    // In serverless mode, free models are not available - use paid version
    if (isServerlessMode() && defaultModel.endsWith(':free')) {
      return defaultModel.replace(':free', '');
    }
    return defaultModel;
  }

  /**
   * Formats model ID into human-readable label
   *
   * @param model - Raw model ID (e.g., "gemma:7b", "meta-llama/llama-3.1-8b-instruct:free")
   * @returns Formatted display label
   *
   * @example
   * formatModelLabel("gemma:7b") // Returns "Gemma 7B"
   * formatModelLabel("llama:3.1-8b-instruct") // Returns "Llama 3.1-8B-INSTRUCT"
   * formatModelLabel("meta-llama/llama-3.1-8b-instruct:free") // Returns "Llama 3.1 8b Instruct (Free*)"
   */
  static formatModelLabel(model: string): string {
    // Handle Ollama models (format: name:version)
    if (model.includes(':')) {
      const [name, version] = model.split(':');
      return `${name.charAt(0).toUpperCase() + name.slice(1)} ${version.toUpperCase()}`;
    }

    // Handle OpenRouter models (format: provider/model-name or provider/model-name:free)
    if (model.includes('/')) {
      const parts = model.split('/');
      const modelName = parts[parts.length - 1].replace(/:free$/, '');
      return modelName
        .split('-')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    }

    return model;
  }

  /**
   * Gets fallback models for a provider (used when API is unavailable)
   *
   * @param provider - LLM provider
   * @returns Array of model options with value, label, and disabled flag
   *
   * @example
   * getFallbackModels("ollama")
   * // Returns: [{ value: "gemma:7b", label: "Gemma 7B", disabled: false }, ...]
   */
  static getFallbackModels(provider: LLMProvider): SelectOption[] {
    // First try to use LLM_MODELS which has the full list with proper disabled flags
    // Defensive check: ensure LLM_MODELS and provider's array exist
    const providerModels = LLM_MODELS?.[provider];
    if (Array.isArray(providerModels) && providerModels.length > 0) {
      // Map the structure from {id, name} to {value, label, disabled}
      return providerModels.map((model: SelectOption) => ({
        value: model.id || model.value,
        label: model.name || model.label,
        disabled: model.disabled || false,
      }));
    }
    // No second list. LLM_FALLBACK_MODELS used to be consulted here and was
    // `{}` in every build ever shipped: the TS generator prefers
    // constants/data/shared_constants_generated.json, which never carried an
    // `llm_fallback_models` key, so `sharedConstants.llm_fallback_models || {}`
    // always took the `{}` branch. The hand-written section in the other JSON
    // that it was meant to read held three OpenRouter ids that 404 today, all
    // marked `disabled: true` -- so the best case for that branch was a dropdown
    // of three unselectable dead models. LLM_MODELS above is generated from
    // AVAILABLE_MODELS and is the one offline list (RULE #-2).
    return [];
  }

  /**
   * Fetches available models from API for a given provider
   *
   * @param provider - LLM provider
   * @param apiUrl - API base URL (e.g., from VITE_SERVERLESS_API_URL)
   * @param context - Usage context: "video" for script generation (15 curated models),
   *                  "chat" for chat mode (ALL models including coding, vision, etc.)
   * @returns Array of model options or fallback models if API fails
   *
   * @example
   * const models = await ModelService.fetchModels("ollama", "http://localhost:5001");
   * // Returns: [{ value: "gemma:7b", label: "Gemma 7B", disabled: false }, ...]
   *
   * @example
   * // For chat mode - get ALL models
   * const chatModels = await ModelService.fetchModels("openrouter", apiUrl, "chat");
   */
  static async fetchModels(
    provider: LLMProvider,
    apiUrl: string,
    context: 'video' | 'chat' = 'video',
    videoSource?: string
  ): Promise<LLMModelOption[]> {
    try {
      // First, try to fetch from the API with context parameter
      const videoSourceParam =
        videoSource !== undefined ? `&video_source=${encodeURIComponent(videoSource)}` : '';
      const response = await fetch(
        `${apiUrl}/api/v1/config/llm-models/${provider}?context=${context}${videoSourceParam}`,
        { headers: await getAuthHeadersFromStorage() }
      );

      if (response.ok) {
        const result = await response.json();

        // API returns {success: true, data: [...]} format
        const models = result.data || result;

        // API returns objects with {value, label, disabled, price_input, price_output}
        // Check if the response is already in the correct format
        if (Array.isArray(models) && models.length > 0 && typeof models[0] === 'object') {
          // API returns objects - use them directly
          return models as LLMModelOption[];
        }

        // Fallback: if API returns strings (old format)
        if (!Array.isArray(models)) {
          console.warn('Unexpected response format:', result);
          return this.getFallbackModels(provider);
        }

        const modelOptions: SelectOption[] = models.map((model: string) => ({
          value: model,
          label: this.formatModelLabel(model),
          disabled: false,
        }));
        return modelOptions;
      } else {
        // Fallback to hardcoded models if API fails
        console.warn('Failed to fetch models from API, using fallback');
        return this.getFallbackModels(provider);
      }
    } catch (error) {
      console.error('Error fetching models:', error);
      // Use fallback models
      return this.getFallbackModels(provider);
    }
  }

  /**
   * Fetches the default model for a provider from the API
   *
   * @param provider - LLM provider
   * @param apiUrl - API base URL
   * @param videoSource - Active video source (e.g. 'ai_deforum'). When provided, the API
   *                       may return a video_source-specific default instead of the global one.
   * @returns Promise with default model ID
   *
   * @example
   * const defaultModel = await ModelService.fetchDefaultModel("openrouter", apiUrl);
   * // Returns "google/gemini-2.5-flash-lite" (global default)
   *
   * @example
   * const deforumDefault = await ModelService.fetchDefaultModel("openrouter", apiUrl, "ai_deforum");
   * // Returns "meta-llama/llama-3.3-70b-instruct" (cheapest Deforum-capable model)
   */
  static async fetchDefaultModel(
    provider: LLMProvider,
    apiUrl: string,
    videoSource?: string
  ): Promise<string | null> {
    try {
      const videoSourceParam =
        videoSource !== undefined ? `?video_source=${encodeURIComponent(videoSource)}` : '';
      const response = await fetch(
        `${apiUrl}/api/v1/config/llm-models/${provider}/default${videoSourceParam}`,
        { headers: await getAuthHeadersFromStorage() }
      );

      if (response.ok) {
        const result = await response.json();
        // API returns {success: true, data: {model: "..."}} format
        const data = result.data || result;
        return data.model;
      } else {
        console.warn('[ModelService] Failed to fetch default model from API');
        return null;
      }
    } catch (error) {
      console.error('[ModelService] Error fetching default model:', error);
      return null;
    }
  }

  /**
   * Gets the default model for a provider, preferring API default, then specific models
   *
   * @param provider - LLM provider
   * @param availableModels - Array of available models
   * @param apiDefault - Optional default model from API (recommended)
   * @returns Default model value
   *
   * @example
   * // With API default (recommended)
   * getDefaultModel("openrouter", models, "meta-llama/llama-3.3-70b-instruct:free")
   * // Returns API default if available in models
   *
   * // Without API default (fallback)
   * getDefaultModel("openrouter", models)
   * // Returns first enabled model
   */
  static getDefaultModel(
    provider: LLMProvider,
    availableModels: LLMModelOption[],
    apiDefault?: string
  ): string {
    if (availableModels.length === 0) {
      return this.getInitialModel(provider);
    }

    // Filter out disabled models
    const enabledModels = availableModels.filter((m) => !m.disabled);

    if (enabledModels.length === 0) {
      console.warn('[ModelService] No enabled models available, using initial model');
      return this.getInitialModel(provider);
    }

    // PRIORITY 1 (serverless): Cheapest non-free model for website users
    // Website users should always start with the most affordable paid model
    if (isServerlessMode() && provider !== 'ollama') {
      const cheapest = this.findCheapestModel(enabledModels);
      if (cheapest) {
        console.log(`[ModelService] Using cheapest paid model (serverless): ${cheapest}`);
        return cheapest;
      }
    }

    // PRIORITY 2: Use API default if provided and available
    // Skip free models in serverless mode (they are web-locked and not usable)
    if (apiDefault) {
      const isFreeLocked = isServerlessMode() && apiDefault.includes(':free');
      if (!isFreeLocked) {
        const apiDefaultModel = enabledModels.find((m) => m.value === apiDefault);
        if (apiDefaultModel) {
          console.log(`[ModelService] Using API default: ${apiDefault}`);
          return apiDefaultModel.value;
        } else {
          console.warn(`[ModelService] API default ${apiDefault} not found in enabled models`);
        }
      } else {
        console.log(`[ModelService] Skipping free API default in serverless: ${apiDefault}`);
      }
    }

    // PRIORITY 3: For Ollama, prefer gemma:7b as fallback
    if (provider === 'ollama') {
      console.log(
        '[Ollama] Available enabled models:',
        enabledModels.map((m) => m.value)
      );
      const gemma7b = enabledModels.find(
        (m) => m.value === 'gemma:7b' || m.value === 'gemma:7b-instruct'
      );
      if (gemma7b) {
        console.log('[Ollama] Setting default model to gemma:7b');
        return gemma7b.value;
      } else {
        console.log(
          '[Ollama] gemma:7b not found, using first enabled model:',
          enabledModels[0].value
        );
        return enabledModels[0].value;
      }
    }

    // PRIORITY 4: Use first enabled model
    // In serverless mode, skip free models (they are web-locked and not usable)
    if (isServerlessMode()) {
      const firstPaid = enabledModels.find((m) => !m.value.endsWith(':free'));
      if (firstPaid) {
        console.log(`[ModelService] Using first paid model (serverless): ${firstPaid.value}`);
        return firstPaid.value;
      }
    }
    console.log(`[ModelService] Using first enabled model: ${enabledModels[0].value}`);
    return enabledModels[0].value;
  }

  /**
   * Finds the cheapest non-free model from a list of enabled models.
   * Uses the backend price each model row carries (input + output cost per
   * million tokens).
   *
   * @param enabledModels - Array of enabled model options
   * @returns Model value of the cheapest model, or null if none found
   */
  private static findCheapestModel(enabledModels: LLMModelOption[]): string | null {
    let cheapest: { value: string; cost: number } | null = null;
    for (const model of enabledModels) {
      if (model.value.endsWith(':free')) continue;
      const cost = getModelTotalPrice(model);
      // Skip models with unknown pricing (e.g. openrouter/auto)
      if (cost === null) continue;
      // Skip $0-priced models: these are free/stealth models whose OpenRouter id
      // lacks the ":free" suffix (e.g. "openrouter/owl-alpha"). They escape the
      // ":free" filter above and would otherwise win "cheapest paid" with cost 0,
      // overriding the intended recommended default (gemini-2.5-flash-lite) on the
      // website. A $0 model is not a paid model, so it does not belong here.
      if (cost <= 0) continue;
      if (!cheapest || cost < cheapest.cost) {
        cheapest = { value: model.value, cost };
      }
    }
    return cheapest?.value ?? null;
  }
}
